import { FC, useEffect, useState } from 'react';
import { ExportIcon } from 'assets/icons';
import { Button } from '@mui/material';
import { If, Loader, Pagination } from 'components';
import PayRunsTable from './components/PayRunsTable';
import PayRunsFilter from './components/PayRunsFilter';
import { usePaginatedGetPayRunsQuery } from './hooks/usePaginatedGetPayRunsQuery';
import { useLazyGetPayRunsExportQuery } from './api';
import { useAppSelector } from 'hooks';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { logAnalyticEvent } from 'utils/analytics';
import { PayRunsCleverTapEvents } from './events';

const PayRunsList: FC = () => {
  const [status, setStatus] = useState<string[]>([]);
  const [payPeriodFrom, setPayPeriodFrom] = useState<string | null>(null);
  const [payPeriodTo, setPayPeriodTo] = useState<string | null>(null);
  const currentTeam = useAppSelector(selectCurrentTeam);
  const [getPayRunsExport, { isLoading: isExporting }] = useLazyGetPayRunsExportQuery();

  const {
    list: payRunsList,
    isLoading,
    loadMore,
    totalListCount,
    page
  } = usePaginatedGetPayRunsQuery(status, payPeriodFrom, payPeriodTo);

  useEffect(() => {
    logAnalyticEvent(PayRunsCleverTapEvents.webPayRunsListView);
  }, []);

  const handleExport = () => {
    logAnalyticEvent(PayRunsCleverTapEvents.webPayRunsExport);
    if (!currentTeam?.id || payRunsList.length === 0) return;

    getPayRunsExport({
      teamId: currentTeam.id,
      status: status.length > 0 ? status : null,
      payPeriodFrom,
      payPeriodTo
    });
  };

  return (
    <div className="flex h-full w-full flex-col py-7">
      <div className="mb-6 px-7">
        <h1 className="text-3xl font-bold text-primaryText">Pay Runs</h1>
      </div>
      <div className="mb-6 flex items-center justify-between px-7">
        <PayRunsFilter
          status={status}
          setStatus={setStatus}
          payPeriodFrom={payPeriodFrom}
          setPayPeriodFrom={setPayPeriodFrom}
          payPeriodTo={payPeriodTo}
          setPayPeriodTo={setPayPeriodTo}
        />
        <Button
          className={`cursor:pointer min-h-[38px] bg-[#A4D7FA]
          px-4 py-2 font-lato font-normal normal-case text-primaryText shadow-none hover:bg-[#80C3F1] 
          ${payRunsList?.length === 0 ? 'opacity-70' : ''}`}
          startIcon={<ExportIcon className="shrink-0" />}
          onClick={handleExport}
          disabled={isExporting || payRunsList?.length === 0}
          disableRipple={payRunsList?.length === 0}>
          <div className="text-sbase font-semibold">{isExporting ? 'Exporting...' : 'Export'}</div>
        </Button>
      </div>
      <If condition={isLoading}>
        <Loader />
      </If>
      <If condition={!isLoading && payRunsList.length > 0}>
        <PayRunsTable payRuns={payRunsList} />

        <Pagination currentPage={page} totalCount={totalListCount} onPageChange={loadMore} limit={50} />
      </If>
      <If condition={!isLoading && payRunsList.length === 0}>
        <div className="flex h-full w-full items-center justify-center">
          <div className="text-sbase font-semibold text-primaryText">No pay runs found</div>
        </div>
      </If>
    </div>
  );
};

export default PayRunsList;
